/*
 * Inspiration gallery — session generation history + free commercial stock search.
 */
(() => {
  const HISTORY_KEY = 'wallprint_ai_gen_history';
  const HISTORY_REMOVED_KEY = 'wallprint_ai_gen_history_removed';
  const SAVED_LIBRARY_KEY = 'wallprint_ai_design_library';
  const PLACEMENT_KEY = 'wallprint_design_hub_placement';
  const PANEL_HEIGHT_KEY = 'wallprint_design_hub_panel_h';
  const WIDE_PREVIEW_ROW_KEY = 'wallprint_wide_preview_row_h_v2';
  const WIDE_LIBRARY_ROW_KEY = 'wallprint_wide_library_row_h_v1';
  const PANEL_RESIZE_GAIN = 1.85;
  const MAX_HISTORY = 200;

  /** Keep in sync with API wallpaperPrompt.js — block room/mockup only; photographic scenery is allowed. */
  const LIBRARY_BLOCK_PATTERN =
    /\b(person|people|portrait|face|woman|man|girl|boy|human|model|selfie|crowd|child|children|living\s*room|bed\s*room|dining\s*room|nursery|kids?\s*'?s?\s*room|kitchen|bathroom|sofa|couch|armchair|furniture|interior|interiors|mural|murals|feature\s+wall|accent\s+wall|room|rooms|interior\s+(scene|photo|mockup|render|view)|room\s+(scene|photo|mockup|interior|view|feature)|mockup|mock-up|photorealistic\s+interior|window\s+view|virtual\s+staging|on\s+the\s+wall|staging|photo\s+of\s+a\s+room|room\s+with\s+furniture)\b/i;

  const FORBIDDEN_PROMPT_PATTERN =
    /\b(interior|interiors|room|rooms|bedroom|living\s*room|dining\s*room|nursery|kitchen|bathroom|sofa|couch|armchair|furniture|mockup|feature\s+wall|accent\s+wall|on\s+the\s+wall|staging|window|mural|murals|photo\s+of\s+a\s+room|room\s+with\s+furniture)\b/i;

  const SCRUB_REPLACEMENTS = [
    [/\bfeature\s+wall(s)?\b/gi, 'large-format wallpaper print'],
    [/\baccent\s+wall\b/gi, 'accent colour palette'],
    [/\bluxury\s+interior\b/gi, 'luxury aesthetic'],
    [/\bspa\s+interior\b/gi, 'spa mood'],
    [/\bmodern\s+interior\b/gi, 'modern aesthetic'],
    [/\bcontemporary\s+interior\b/gi, 'contemporary style'],
    [/\bcosy\s+interior\b/gi, 'cosy mood'],
    [/\bcozy\s+interior\b/gi, 'cozy mood'],
    [/\b(medium|light|dark|deep|soft|warm|cool)\s+to\s+(medium|light|dark|deep|soft|warm|cool)\s+interior\b/gi, '$1 to $2 tonal palette'],
    [/\binterior\s+mural\b/gi, 'decorative full-bleed pattern'],
    [/\binterior\b/gi, ''],
    [/\bmural\s+wallpaper\b/gi, 'wallpaper pattern'],
    [/\b(\w+)\s+mural\b/gi, '$1 wallpaper pattern'],
    [/\bmural\b/gi, 'wallpaper pattern'],
    [/\bliving\s*room\b/gi, ''],
    [/\bbedroom\b/gi, ''],
    [/\bnursery\b/gi, ''],
    [/\bdining\s*room\b/gi, ''],
    [/\bkitchen\b/gi, ''],
    [/\bbathroom\b/gi, ''],
    [/\bwindows?\b/gi, ''],
    [/\b(sofa|couch|armchair|furniture)\b/gi, ''],
    [/\bphotographic\s+scene\s+of\b/gi, 'photorealistic wallpaper surface texture of'],
    [/\bphoto(graphic)?\s+scene\s+of\b/gi, 'photorealistic wallpaper surface texture of'],
    [/\bon the wall\b/gi, ''],
    [/\broom\b/gi, ''],
  ];

  const collapsePromptWhitespace = (text) =>
    String(text || '')
      .replace(/\bcalm\s+calm\b/gi, 'calm')
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+,/g, ',')
      .replace(/,\s*,/g, ',')
      .replace(/^,\s*|\s*,$/g, '')
      .trim();

  const scrubWallpaperLibraryPrompt = (raw) => {
    let text = String(raw || '').trim();
    if (!text) return '';
    for (const [re, repl] of SCRUB_REPLACEMENTS) text = text.replace(re, repl);
    return collapsePromptWhitespace(text);
  };

  const hasForbiddenPromptTerms = (text) => FORBIDDEN_PROMPT_PATTERN.test(String(text || ''));

  const isLibraryItemSuitable = (item) => {
    const prompt = scrubWallpaperLibraryPrompt(item?.prompt);
    if (!prompt || hasForbiddenPromptTerms(prompt)) return false;
    const blob = [item?.title, prompt, item?.credit || ''].filter(Boolean).join(' ');
    return !LIBRARY_BLOCK_PATTERN.test(blob);
  };

  const normalizeLibraryItem = (item) => {
    if (!item) return null;
    const prompt = scrubWallpaperLibraryPrompt(item.prompt);
    if (!prompt || hasForbiddenPromptTerms(prompt)) return null;
    const normalized = { ...item, prompt };
    return isLibraryItemSuitable(normalized) ? normalized : null;
  };

  const filterLibraryItems = (items) =>
    (Array.isArray(items) ? items.map(normalizeLibraryItem).filter(Boolean) : []);

  const TRENDING_SEED = [
    'marble',
    'tropical',
    'botanical',
    'abstract',
    'geometric',
    'minimal',
    'floral',
    'forest',
    'watercolour',
    'vintage',
  ];

  const tokenize = (query) =>
    String(query || '')
      .toLowerCase()
      .split(/[^\p{L}\p{N}]+/u)
      .filter((w) => w.length > 1);

  const scoreText = (haystack, tokens) => {
    if (!tokens.length) return 1;
    const hay = haystack.toLowerCase();
    let score = 0;
    for (const token of tokens) {
      if (hay.includes(token)) score += token.length > 3 ? 2 : 1;
    }
    return score;
  };

  const scoreItem = (item, tokens) => {
    const parts = [item.title, item.prompt, ...(item.tags || []), item.credit || ''].filter(Boolean);
    return scoreText(parts.join(' '), tokens);
  };

  const parseHexColor = (input) => {
    const raw = String(input || '').trim().replace(/^#/, '');
    if (!/^[0-9a-f]{3}$/i.test(raw) && !/^[0-9a-f]{6}$/i.test(raw)) return null;
    const hex =
      raw.length === 3
        ? raw
            .split('')
            .map((c) => c + c)
            .join('')
        : raw;
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  };

  const rgbDistance = (a, b) => {
    const dr = a.r - b.r;
    const dg = a.g - b.g;
    const db = a.b - b.b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  };

  const scorePaletteMatch = (item, palette) => {
    if (!palette?.primary) return 0;
    const target = parseHexColor(palette.primary);
    if (!target) return 0;
    const itemPalette = item.metadata?.palette || item.palette;
    const itemPrimary = parseHexColor(itemPalette?.primary || itemPalette?.color);
    if (itemPrimary) {
      const dist = rgbDistance(target, itemPrimary);
      return Math.max(0, 1 - dist / 441);
    }
    return scoreText(
      [item.title, item.prompt, ...(item.tags || [])].join(' '),
      tokenize(palette.primary.replace('#', ''))
    );
  };

  const readHistory = () => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const writeHistory = (items) => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(items.slice(0, MAX_HISTORY)));
    } catch {
      /* quota */
    }
  };

  const readRemovedHistoryIds = () => {
    try {
      const raw = localStorage.getItem(HISTORY_REMOVED_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  };

  const writeRemovedHistoryIds = (ids) => {
    try {
      localStorage.setItem(HISTORY_REMOVED_KEY, JSON.stringify([...new Set(ids.map(String))].slice(0, 500)));
    } catch {
      /* quota */
    }
  };

  const readSavedLibrary = () => {
    try {
      const raw = localStorage.getItem(SAVED_LIBRARY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  class InspirationGallery {
    /** @param {HTMLElement} root */
    constructor(galleryEl) {
      this.root = galleryEl;
      this.genRoot = galleryEl.closest('[data-ai-generator]');
      this.gen = this.genRoot?.wallprintAiGenerator ?? null;

      this.searchInput = galleryEl.querySelector('[data-ai-inspiration-search]');
      this.grid = galleryEl.querySelector('[data-ai-inspiration-grid]');
      this.emptyEl = galleryEl.querySelector('[data-ai-inspiration-empty]');
      this.statusEl = galleryEl.querySelector('[data-ai-inspiration-status]');
      this.historyCountEl = galleryEl.querySelector('[data-ai-inspiration-history-count]');
      this.historyStripEl = galleryEl.querySelector('[data-ai-inspiration-history-strip]');
      this.savedCountEl = galleryEl.querySelector('[data-ai-inspiration-saved-count]');
      this.savedStripEl = galleryEl.querySelector('[data-ai-inspiration-saved-strip]');
      this.similarStripEl = galleryEl.querySelector('[data-ai-inspiration-similar-strip]');
      this.inspirationStripEl = galleryEl.querySelector('[data-ai-inspiration-inspiration-strip]');
      this.communityStripEl = galleryEl.querySelector('[data-ai-inspiration-community-strip]');
      this.storeStripEl = galleryEl.querySelector('[data-ai-inspiration-store-strip]');
      this.searchWrapEl = galleryEl.querySelector('[data-ai-inspiration-search-wrap]');
      this.tagsWrapEl = galleryEl.querySelector('[data-ai-inspiration-tags-wrap]');
      this.tagsEl = galleryEl.querySelector('[data-ai-inspiration-tags]');
      this.footnoteEl = galleryEl.querySelector('[data-ai-inspiration-footnote]');
      this.tabs = Array.from(galleryEl.querySelectorAll('[data-ai-inspiration-tab]'));
      this.toggleBtn = galleryEl.querySelector('[data-ai-inspiration-toggle]');
      this.headToggle = galleryEl.querySelector('[data-ai-inspiration-head-toggle]');
      this.panel = galleryEl.querySelector('[data-ai-inspiration-panel]');
      this.navEl = galleryEl.querySelector('.ai-generator__design-hub-nav');
      this.belowSlot = galleryEl.querySelector('[data-ai-design-hub-below-slot]');
      const dockScope = galleryEl.closest('.ai-generator__design-grid') || galleryEl.closest('.ai-generator');
      this.hubResizeBar = galleryEl.querySelector('[data-ai-design-hub-resize-bar]');
      this.hubResizeTrack = galleryEl.querySelector('[data-ai-design-hub-resize-track]');
      this.resizeHandle =
        dockScope?.querySelector('[data-ai-design-hub-resize]') ||
        galleryEl.querySelector('[data-ai-design-hub-resize]');
      this.splitterBar = dockScope?.querySelector('[data-ai-preview-hub-splitter]');
      this.splitterTrack = dockScope?.querySelector('[data-ai-preview-hub-splitter-track]');
      this.panelInner = galleryEl.querySelector('.ai-generator__design-hub-panel-inner');
      this.overlayHost = this.genRoot?.querySelector('[data-ai-design-hub-overlay-host]');
      this.placementBtns = Array.from(galleryEl.querySelectorAll('[data-ai-design-hub-placement]'));

      this.expanded = false;
      this.placement = 'below';
      this.catalogUrl = galleryEl.dataset.catalogUrl || `${window.Shopify?.routes?.root || '/'}assets/ai-inspiration-catalog.json`;
      this.searchEndpoint = galleryEl.dataset.inspirationSearchEndpoint || '';
      this.designLibraryEndpoint =
        galleryEl.dataset.designLibraryEndpoint ||
        this.searchEndpoint.replace(/\/search\/?$/, '/design-library');
      this.communityGalleryEndpoint =
        galleryEl.dataset.communityGalleryEndpoint ||
        this.searchEndpoint.replace(/\/search\/?$/, '/community-gallery');
      this.storeEndpoint = this.searchEndpoint.replace(/\/search\/?$/, '/store');
      this.shopUrl = galleryEl.dataset.shopUrl || '';
      this.similarEndpoint = this.searchEndpoint.replace(/\/search\/?$/, '/similar');
      this.imageEndpoint = this.searchEndpoint.replace(/\/search\/?$/, '/image');
      this.storefrontKey = galleryEl.dataset.storefrontApiKey || '';

      this.tab = 'history';
      this.freeCatalog = [];
      this.storeCatalog = [];
      this.communityCatalog = [];
      this.history = readHistory();
      this.visualQuery = null;
      this._visualResults = null;
      this._visualTimer = 0;
      this._searchTimer = 0;
      this._renderToken = 0;
      this._loading = false;
      this.trendingTags = [];
      this._activeTag = '';
      this._panelHeight = 0;
      this._panelResizeMin = 160;
      this._panelResizeMax = 620;
      this._activePalette = null;

      this.#bindEvents();
      this.#initPlacement();
      this.#initPanelHeight();
      this.#initWidePreviewRowHeight();
      this.genRoot?.addEventListener('wallprint:layout-mode-changed', () => {
        this.#initWidePreviewRowHeight();
        this.#syncPreviewColumnHubState();
        this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      });
      this.#bindPanelResize();
      this.#syncTabChrome();
      this.#syncSavedCount();
      this.#syncNavStrips();
      this.#syncPanelPlacement();
      this.gen?.syncWideViewDock?.();
      this.#loadCatalog().then(() =>
        this.#loadStoreCatalog().then(() =>
          this.#loadCommunityCatalog().then(() =>
            this.#loadServerHistory().then(() => {
              this.#syncNavStrips();
              this.#render();
            })
          )
        )
      );
    }

    /** Open a library tab and expand the hub (used by toolbar Save / Compare). */
    openTab(tab = 'saved', { expand = true } = {}) {
      const target = this.tabs.find((t) => t.dataset.aiInspirationTab === tab) || this.tabs[0];
      if (target) target.click();
      else {
        this.tab = tab;
        this.#syncTabChrome();
        this.#render();
      }
      if (expand) this.#setExpanded(true);
    }

    #bindEvents() {
      this.toggleBtn?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.#setExpanded(!this.expanded);
      });

      this.headToggle?.addEventListener('click', (event) => {
        if (event.target.closest('.ai-generator__design-hub-head-actions')) return;
        if (event.target.closest('[data-ai-inspiration-toggle]')) return;
        this.#setExpanded(!this.expanded);
      });

      this.headToggle?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        if (event.target.closest('.ai-generator__design-hub-head-actions')) return;
        this.#setExpanded(!this.expanded);
      });

      this.searchInput?.addEventListener('input', () => {
        const q = (this.searchInput?.value || '').trim().toLowerCase();
        if (this.tab === 'inspiration') {
          this._activeTag = this.trendingTags.includes(q) ? q : '';
        } else {
          this._activeTag = this.trendingTags.includes(q) ? q : '';
        }
        this.#renderTrendingTags();
        window.clearTimeout(this._searchTimer);
        this._searchTimer = window.setTimeout(() => this.#render(), 180);
      });

      this.searchInput?.addEventListener('focus', () => {
        if (!this.expanded) this.#setExpanded(true);
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.tab = tab.dataset.aiInspirationTab || 'history';
          this.tabs.forEach((t) => {
            const active = t === tab;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          if (this.tab === 'history' || this.tab === 'inspiration') {
            this._activeTag = '';
          }
          if (this.tab === 'inspiration' && this.searchInput) {
            this.searchInput.value = '';
          }
          this.#syncTabChrome();
          if (!this.expanded) this.#setExpanded(true);
          this.#render();
        });
      });

      this.tagsEl?.addEventListener('click', (event) => {
        const btn = /** @type {HTMLElement | null} */ (event.target.closest('[data-ai-inspiration-tag]'));
        if (!btn) return;
        const isAll = btn.dataset.aiInspirationTagAll === 'true';
        const tag = btn.dataset.aiInspirationTag || '';
        if (isAll) {
          this._activeTag = '';
          if (this.searchInput) this.searchInput.value = '';
        } else {
          this._activeTag = this._activeTag === tag ? '' : tag;
          if (this.searchInput) this.searchInput.value = this._activeTag;
        }
        this.#renderTrendingTags();
        this.#render();
      });

      this.grid?.addEventListener('click', (event) => {
        const action = /** @type {HTMLElement | null} */ (event.target.closest('[data-ai-inspiration-action]'));
        if (action?.dataset.aiInspirationAction === 'restore-saved') {
          void this.genRoot?.wallprintAiDesignSave?.restoreDesign?.(action.dataset.aiSavedId);
          return;
        }
        if (action?.dataset.aiInspirationAction === 'remove-saved') {
          this.genRoot?.wallprintAiDesignSave?.removeDesign?.(action.dataset.aiSavedId);
          return;
        }
        if (action?.dataset.aiInspirationAction === 'compare-saved') {
          this.genRoot?.wallprintAiDesignSave?.addSavedToCompare?.(action.dataset.aiSavedId);
          return;
        }

        const card = /** @type {HTMLElement | null} */ (
          event.target.closest('[data-ai-inspiration-item], [data-ai-saved-item]')
        );
        if (!card) return;
        const item = card.dataset.aiSavedItem
          ? JSON.parse(card.dataset.aiSavedItem || '{}')
          : this.#itemFromCard(card);
        if (!item || !Object.keys(item).length) return;
        if (action?.dataset.aiInspirationAction === 'preview' && item.source === 'history') {
          this.#restoreHistoryItem(item);
          return;
        }
        if (action?.dataset.aiInspirationAction === 'delete' && item.source === 'history') {
          this.#removeHistoryItem(item.id || item.ai_image_id);
          return;
        }
        if (action?.dataset.aiInspirationAction === 'product' && item.product_url) {
          window.open(item.product_url, '_blank', 'noopener,noreferrer');
          return;
        }
        if (action?.dataset.aiInspirationAction === 'shop' && item.shop_url) {
          window.open(item.shop_url, '_blank', 'noopener,noreferrer');
          return;
        }
        if (action?.dataset.aiInspirationAction === 'order' && item.source === 'community') {
          void this.#orderCommunityItem(item);
          return;
        }
        this.#applyPrompt(item);
      });

      this.gen?.promptInput?.addEventListener('input', () => {
        if (document.activeElement !== this.gen?.promptInput) return;
        if (this.searchInput) this.searchInput.value = this.gen.promptInput.value.trim();
        window.clearTimeout(this._searchTimer);
        this._searchTimer = window.setTimeout(() => this.#render(), 220);
      });

      this.genRoot?.addEventListener('wallprint:palette-changed', (event) => {
        this._activePalette = /** @type {CustomEvent} */ (event).detail?.palette || null;
        this.#render();
      });

      this.genRoot?.addEventListener('wallprint:community-gallery-changed', () => {
        void this.#loadCommunityCatalog().then(() => {
          if (this.tab === 'inspiration') this.#render();
          this.#syncNavStrips();
        });
      });

      this.genRoot?.addEventListener('wallprint:design-library-changed', () => {
        this.#syncSavedCount();
        if (this.tab === 'saved') this.#render();
        this.#syncNavStrips();
      });

      this.genRoot?.addEventListener('wallprint:open-design-saved', () => {
        this.openTab('saved', { expand: true });
      });

      this.genRoot?.addEventListener('wallprint:generation-complete', (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        if (detail?.historyItem) this.#recordHistory(detail.historyItem);
        void this.#loadServerHistory();
      });

      this.genRoot?.addEventListener('wallprint:prompt-changed', () => {
        if (document.activeElement !== this.searchInput && this.tab !== 'inspiration') {
          this.searchInput.value = this.gen?.promptInput?.value?.trim() || '';
        }
        this.#render();
      });

      this.genRoot?.addEventListener('wallprint:preview-changed', (event) => {
        this.visualQuery = /** @type {CustomEvent} */ (event).detail?.asset || null;
        window.clearTimeout(this._visualTimer);
        this._visualTimer = window.setTimeout(() => {
          this.#prefetchSimilarPreviews().then(() => this.#syncNavStrips());
          this.#render();
        }, 320);
      });

      this.genRoot?.addEventListener('wallprint:preview-layout', () => this.#syncPanelPlacement());

      this.placementBtns.forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.stopPropagation();
          const mode = btn.dataset.aiDesignHubPlacement === 'overlay' ? 'overlay' : 'below';
          this.#setPlacement(mode);
          if (!this.expanded) this.#setExpanded(true);
        });
      });

      this.genRoot?.addEventListener('wallprint:layout-mode-changed', () => {
        if (this.genRoot?.classList.contains('is-wide-view') && this.placement === 'overlay') {
          this.#setPlacement('below');
        }
        this.refreshDockRefs();
        this.#initWideLibraryRowHeight();
      });
    }

    /** Re-resolve splitter nodes after wide-view dock moves DOM. */
    refreshDockRefs() {
      const scope = this.genRoot || document;
      this.splitterBar = scope.querySelector('[data-ai-preview-hub-splitter]');
      this.splitterTrack = scope.querySelector('[data-ai-preview-hub-splitter-track]');
      this.resizeHandle = scope.querySelector('[data-ai-design-hub-resize]');
      this.#syncPanelResizeUi();
      this.#bindPanelResizeIfNeeded();
      this.#initWideLibraryRowHeight();
    }

    #bindPanelResizeIfNeeded() {
      const resizeTarget = this.splitterTrack || this.resizeHandle;
      if (!resizeTarget || this._panelResizeBoundTarget === resizeTarget) return;
      this._panelResizeBoundTarget = resizeTarget;
      resizeTarget.addEventListener('pointerdown', (event) =>
        this.#startPanelResize(event, resizeTarget)
      );
    }

    _panelResizeBoundTarget = null;

    #initPanelHeight() {
      this.#syncPanelHeightLimits();
      try {
        const stored = Number(localStorage.getItem(PANEL_HEIGHT_KEY));
        if (Number.isFinite(stored) && stored >= this._panelResizeMin && stored <= this._panelResizeMax) {
          this._panelHeight = Math.round(stored);
        }
      } catch {
        /* ignore */
      }
      this.#applyPanelHeight();
      this.#initWideLibraryRowHeight();
    }

    #syncPanelHeightLimits() {
      this._panelResizeMin = 160;
      this._panelResizeMax = Math.round(Math.min(window.innerHeight * 0.72, 720));
    }

    #defaultWideLibraryRowHeight() {
      return Math.round(Math.min(window.innerHeight * 0.32, 360));
    }

    #wideLibraryRowLimits() {
      const vh = window.innerHeight;
      return {
        min: 148,
        max: Math.round(Math.min(vh * 0.82, 1100)),
      };
    }

    #applyWideLibraryRowHeight(h, { persist = true } = {}) {
      const grid = this.genRoot?.querySelector('.ai-generator__design-grid');
      if (!grid || !this.#isWideViewGrid()) return;
      const { min, max } = this.#wideLibraryRowLimits();
      const clamped = Math.min(max, Math.max(min, Math.round(h)));
      this._wideLibraryRowH = clamped;
      grid.style.setProperty('--ai-wide-library-row-h', `${clamped}px`);
      if (persist) {
        try {
          localStorage.setItem(WIDE_LIBRARY_ROW_KEY, String(clamped));
        } catch {
          /* ignore */
        }
      }
    }

    #initWideLibraryRowHeight() {
      const grid = this.genRoot?.querySelector('.ai-generator__design-grid');
      if (!grid) return;
      if (!this.#isWideViewGrid()) {
        grid.style.removeProperty('--ai-wide-library-row-h');
        this._wideLibraryRowH = 0;
        return;
      }
      try {
        const stored = Number(localStorage.getItem(WIDE_LIBRARY_ROW_KEY));
        const { min, max } = this.#wideLibraryRowLimits();
        if (Number.isFinite(stored) && stored >= min && stored <= max) {
          this.#applyWideLibraryRowHeight(stored, { persist: false });
          return;
        }
      } catch {
        /* ignore */
      }
      grid.style.removeProperty('--ai-wide-library-row-h');
      this._wideLibraryRowH = 0;
    }

    #defaultPanelHeight() {
      return Math.round(Math.min(window.innerHeight * 0.28, 300));
    }

    #applyPanelHeight() {
      const useBelow = this.placement === 'below' && this.expanded;
      if (!useBelow) {
        this.root.style.removeProperty('--ai-design-hub-panel-h');
        this.root.classList.remove('is-hub-panel-sized');
        return;
      }
      const h = this._panelHeight || this.#defaultPanelHeight();
      this._panelHeight = Math.min(this._panelResizeMax, Math.max(this._panelResizeMin, h));
      this.root.style.setProperty('--ai-design-hub-panel-h', `${this._panelHeight}px`);
      this.root.classList.add('is-hub-panel-sized');
    }

    #bindPanelResize() {
      this.#bindPanelResizeIfNeeded();

      window.addEventListener('resize', () => {
        this.#syncPanelHeightLimits();
        if (this._panelHeight > this._panelResizeMax) {
          this._panelHeight = this._panelResizeMax;
          this.#applyPanelHeight();
          this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
        }
        if (this.#isWideViewGrid() && this._wideLibraryRowH > this.#wideLibraryRowLimits().max) {
          this.#applyWideLibraryRowHeight(this.#wideLibraryRowLimits().max);
          this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
        }
      });
    }

    #isWideViewGrid() {
      return Boolean(
        this.genRoot?.classList.contains('is-wide-view') ||
          this.genRoot?.classList.contains('is-preview-expanded')
      );
    }

    #widePreviewRowLimits() {
      const state = this.gen?.measureState?.() || {};
      if (typeof this.gen?.widePreviewRowMinHeight === 'function') {
        const min = this.gen.widePreviewRowMinHeight(state.printWidthCm, state.printHeightCm);
        const vh = window.innerHeight;
        return {
          min,
          max: Math.round(Math.min(vh * 0.92, vh - 72)),
        };
      }
      const vh = window.innerHeight;
      return {
        min: 420,
        max: Math.round(Math.min(vh * 0.92, vh - 72)),
      };
    }

    #applyWidePreviewRowHeight(h, { userSet = true } = {}) {
      const grid = this.genRoot?.querySelector('.ai-generator__design-grid');
      if (!grid || !this.#isWideViewGrid()) return;
      const { min, max } = this.#widePreviewRowLimits();
      const clamped = Math.min(max, Math.max(min, Math.round(h)));
      this._widePreviewRowH = clamped;
      if (userSet) this._widePreviewRowUserSet = true;
      grid.style.setProperty('--ai-wide-preview-row-h', `${clamped}px`);
    }

    #initWidePreviewRowHeight() {
      const grid = this.genRoot?.querySelector('.ai-generator__design-grid');
      if (!grid) return;
      if (!this.#isWideViewGrid()) {
        grid.style.removeProperty('--ai-wide-preview-row-h');
        this._widePreviewRowH = 0;
        this._widePreviewRowUserSet = false;
        return;
      }
      const { min, max } = this.#widePreviewRowLimits();
      const absMin = Math.max(min, 280);
      try {
        const stored = Number(localStorage.getItem(WIDE_PREVIEW_ROW_KEY));
        if (Number.isFinite(stored) && stored >= absMin && stored <= max) {
          this.#applyWidePreviewRowHeight(stored, { userSet: true });
          this.gen?.bootstrapWidePreviewLayout?.();
          return;
        }
        if (Number.isFinite(stored) && stored > 0 && stored < absMin) {
          localStorage.removeItem(WIDE_PREVIEW_ROW_KEY);
          this._widePreviewRowUserSet = false;
        }
      } catch {
        /* ignore */
      }
      grid.style.removeProperty('--ai-wide-preview-row-h');
      this._widePreviewRowH = 0;
      this._widePreviewRowUserSet = false;
      this.gen?.syncWideViewPreviewRow?.();
      this.gen?.bootstrapWidePreviewLayout?.();
    }

    #startWidePreviewRowResize(event, captureTarget) {
      const grid = this.genRoot?.querySelector('.ai-generator__design-grid');
      const studio = grid?.querySelector('.ai-generator__design-studio');
      if (!grid || !studio) return;

      const { min, max } = this.#widePreviewRowLimits();
      const startY = event.clientY;
      const startH = Math.round(studio.getBoundingClientRect().height) || min;
      this.root.classList.add('is-hub-resizing');
      this.genRoot?.classList.add('is-hub-resizing');
      captureTarget.setPointerCapture?.(event.pointerId);

      const onMove = (ev) => {
        const delta = (startY - ev.clientY) * PANEL_RESIZE_GAIN;
        this.#applyWidePreviewRowHeight(startH + delta);
        this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      };

      const onUp = () => {
        this.root.classList.remove('is-hub-resizing');
        this.genRoot?.classList.remove('is-hub-resizing');
        captureTarget.releasePointerCapture?.(event.pointerId);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        try {
          if (this._widePreviewRowH) {
            localStorage.setItem(WIDE_PREVIEW_ROW_KEY, String(this._widePreviewRowH));
          }
        } catch {
          /* ignore */
        }
        this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    }

    #startWideLibraryRowResize(event, captureTarget) {
      const hub = this.root;
      const grid = this.genRoot?.querySelector('.ai-generator__design-grid');
      if (!hub || !grid) return;

      const { min, max } = this.#wideLibraryRowLimits();
      const startY = event.clientY;
      const startH =
        this._wideLibraryRowH ||
        Math.round(hub.getBoundingClientRect().height) ||
        this.#defaultWideLibraryRowHeight();
      this.root.classList.add('is-hub-resizing');
      this.genRoot?.classList.add('is-hub-resizing');
      captureTarget.setPointerCapture?.(event.pointerId);

      const onMove = (ev) => {
        const delta = (startY - ev.clientY) * PANEL_RESIZE_GAIN;
        this.#applyWideLibraryRowHeight(startH + delta, { persist: false });
        this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      };

      const onUp = () => {
        this.root.classList.remove('is-hub-resizing');
        this.genRoot?.classList.remove('is-hub-resizing');
        captureTarget.releasePointerCapture?.(event.pointerId);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        try {
          if (this._wideLibraryRowH) {
            localStorage.setItem(WIDE_LIBRARY_ROW_KEY, String(this._wideLibraryRowH));
          }
        } catch {
          /* ignore */
        }
        this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    }

    #startPanelResize(event, captureTarget) {
      if (this.placement !== 'below' || !this.expanded) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.#isWideViewGrid()) {
        this.#startWideLibraryRowResize(event, captureTarget);
        return;
      }
      this.#syncPanelHeightLimits();
      const startY = event.clientY;
      const startH = this._panelHeight || this.panelInner?.offsetHeight || this.#defaultPanelHeight();
      this.root.classList.add('is-hub-resizing');
      this.genRoot?.classList.add('is-hub-resizing');
      captureTarget.setPointerCapture?.(event.pointerId);

      const onMove = (ev) => {
        const delta = (startY - ev.clientY) * PANEL_RESIZE_GAIN;
        this._panelHeight = Math.min(
          this._panelResizeMax,
          Math.max(this._panelResizeMin, Math.round(startH + delta))
        );
        this.#applyPanelHeight();
        this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      };

      const onUp = () => {
        this.root.classList.remove('is-hub-resizing');
        this.genRoot?.classList.remove('is-hub-resizing');
        captureTarget.releasePointerCapture?.(event.pointerId);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        try {
          if (this._panelHeight) localStorage.setItem(PANEL_HEIGHT_KEY, String(this._panelHeight));
        } catch {
          /* ignore */
        }
        this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    }

    #syncPanelResizeUi() {
      const show = this.placement === 'below' && this.expanded;
      if (this.resizeHandle) this.resizeHandle.hidden = !show;
      if (this.splitterBar) this.splitterBar.hidden = !show;
    }

    #initPlacement() {
      try {
        const stored = localStorage.getItem(PLACEMENT_KEY);
        if (stored === 'overlay' || stored === 'below') this.placement = stored;
      } catch {
        /* ignore */
      }
      this.#syncPlacementUi();
    }

    #setPlacement(mode) {
      this.placement = mode === 'overlay' ? 'overlay' : 'below';
      try {
        localStorage.setItem(PLACEMENT_KEY, this.placement);
      } catch {
        /* ignore */
      }
      this.#syncPlacementUi();
      this.#syncPanelPlacement();
      this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      window.requestAnimationFrame(() => {
        this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      });
    }

    #syncPlacementUi() {
      this.root.classList.toggle('is-hub-overlay', this.placement === 'overlay');
      this.root.classList.toggle('is-hub-below', this.placement === 'below');
      for (const btn of this.placementBtns) {
        const active = btn.dataset.aiDesignHubPlacement === this.placement;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    }

    #syncPreviewColumnHubState() {
      const root = this.genRoot;
      if (!root) return;
      /* Below-mode library lives in the design grid — only overlay shares the preview column. */
      const inPreview = this.expanded && this.placement === 'overlay';
      root.classList.toggle('is-hub-in-preview', inPreview);
    }

    #syncWideOverlayNav(useOverlay) {
      const nav = this.navEl;
      const shell = this.root.querySelector('.ai-generator__design-hub-shell');
      const belowSlot = this.belowSlot;
      const overlayHost = this.overlayHost || this.genRoot?.querySelector('[data-ai-design-hub-overlay-host]');
      const useWideOverlayNav = useOverlay && this.#isWideViewGrid();

      this.genRoot?.classList.toggle('is-hub-wide-overlay-chrome', useWideOverlayNav);

      if (!nav || !shell || !belowSlot) return overlayHost;

      if (useWideOverlayNav && overlayHost) {
        if (nav.parentElement !== overlayHost) {
          overlayHost.insertBefore(nav, overlayHost.firstChild);
        }
        return overlayHost;
      }

      if (nav.parentElement !== shell) {
        shell.insertBefore(nav, belowSlot);
      }

      return overlayHost;
    }

    #syncPanelPlacement() {
      const panel = this.panel;
      const belowSlot = this.belowSlot;
      const overlayHost = this.overlayHost || this.genRoot?.querySelector('[data-ai-design-hub-overlay-host]');
      const grid = this.genRoot?.querySelector('.ai-generator__design-grid');
      if (!panel || !belowSlot) return;

      const useOverlay = this.placement === 'overlay' && this.expanded;
      this.genRoot?.classList.toggle('is-hub-overlay-open', useOverlay);

      if (overlayHost) {
        overlayHost.hidden = !useOverlay;
        overlayHost.setAttribute('aria-hidden', useOverlay ? 'false' : 'true');
      }

      const resolvedOverlayHost = this.#syncWideOverlayNav(useOverlay);
      const target = useOverlay && resolvedOverlayHost ? resolvedOverlayHost : belowSlot;
      if (panel.parentElement !== target) target.appendChild(panel);

      panel.classList.toggle('is-overlay-mode', useOverlay);
      panel.hidden = !this.expanded;

      this.#syncPanelResizeUi();
      this.#applyPanelHeight();
      this.#syncPreviewColumnHubState();
    }

    async #loadCatalog() {
      if (this.searchEndpoint) {
        try {
          const url = this.#apiUrl(this.searchEndpoint.replace(/\/search\/?$/, '/catalog'));
          const res = await fetch(url, { headers: this.#apiHeaders() });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data.items) && data.items.length) {
              this.freeCatalog = this.#sortFeaturedFirst(filterLibraryItems(data.items));
              this.#deriveTrendingTags();
              this.#syncNavStrips();
              return;
            }
          }
        } catch {
          /* fall through to theme asset */
        }
      }

      try {
        const res = await fetch(this.catalogUrl, { cache: 'force-cache' });
        if (res.ok) {
          const data = await res.json();
          this.freeCatalog = this.#sortFeaturedFirst(
            filterLibraryItems(Array.isArray(data) ? data : data.items || [])
          );
        }
      } catch (err) {
        console.warn('[inspiration]', err);
        this.freeCatalog = [];
      }
      this.#deriveTrendingTags();
      this.#syncNavStrips();
    }

    #stripHtml(html) {
      return String(html || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    #mapShopifyProduct(product) {
      const image = product.images?.[0];
      if (!image?.src) return null;
      const shopBase = (this.shopUrl || '').replace(/\/$/, '');
      const tags = Array.isArray(product.tags)
        ? product.tags.map((t) => String(t).trim()).filter(Boolean)
        : String(product.tags || '')
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean);
      const title = String(product.title || 'Store product').trim();
      const type = String(product.product_type || '').trim();
      const excerpt = this.#stripHtml(product.body_html).slice(0, 200);
      const parts = [title];
      if (type) parts.push(type);
      if (tags.length) parts.push(tags.slice(0, 8).join(', '));
      let prompt = parts.filter(Boolean).join(', ');
      if (excerpt.length > 24) prompt = `${prompt}. ${excerpt}`;
      prompt = `${prompt}, WallPrint wallpaper mural, high detail, print-ready composition`;
      const imageUrl = (src, width) => {
        try {
          const u = new URL(src);
          u.searchParams.set('width', String(width));
          return u.toString();
        } catch {
          const join = src.includes('?') ? '&' : '?';
          return `${src}${join}width=${width}`;
        }
      };
      return {
        id: `shop-${product.id}`,
        source: 'store',
        title,
        prompt,
        tags,
        product_type: type,
        handle: String(product.handle || '').trim(),
        thumb_url: imageUrl(image.src, 640),
        preview_url: imageUrl(image.src, 1400),
        product_url: shopBase ? `${shopBase}/products/${product.handle}` : '',
      };
    }

    async #loadStoreCatalog() {
      if (this.storeEndpoint) {
        try {
          const url = this.#apiUrl(this.storeEndpoint);
          const res = await fetch(url, { headers: this.#apiHeaders() });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data.items)) {
              this.storeCatalog = data.items;
              return;
            }
          }
        } catch {
          /* fall through */
        }
      }

      if (!this.shopUrl) return;
      try {
        const res = await fetch(`${this.shopUrl.replace(/\/$/, '')}/products.json?limit=50`);
        if (!res.ok) return;
        const data = await res.json();
        this.storeCatalog = (data.products || []).map((p) => this.#mapShopifyProduct(p)).filter(Boolean);
      } catch (err) {
        console.warn('[inspiration] store catalog', err);
        this.storeCatalog = [];
      }
    }

    async #loadCommunityCatalog() {
      if (!this.communityGalleryEndpoint) return;
      try {
        const url = this.#apiUrl(`${this.communityGalleryEndpoint}?limit=96`);
        const res = await fetch(url, { headers: this.#apiHeaders(), credentials: 'same-origin' });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data.items)) {
          this.communityCatalog = data.items.map((item) => ({ ...item, source: 'community' }));
        }
      } catch (err) {
        console.warn('[inspiration] community gallery', err);
      }
    }

    async #searchCommunity(query) {
      const tokens = tokenize(query);
      const hasColorFilter = Boolean(this._activePalette?.primary);
      if (this.communityGalleryEndpoint && (query.trim().length > 1 || hasColorFilter)) {
        try {
          const url = new URL(this.#apiUrl(this.communityGalleryEndpoint), window.location.origin);
          if (query.trim().length > 1) url.searchParams.set('q', query.trim());
          url.searchParams.set('limit', '48');
          if (hasColorFilter) url.searchParams.set('color', this._activePalette.primary);
          const res = await fetch(url.toString(), { headers: this.#apiHeaders(), credentials: 'same-origin' });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data.items)) {
              return data.items.map((item) => ({ ...item, source: 'community' }));
            }
          }
        } catch {
          /* local fallback */
        }
      }

      const pool = this.communityCatalog;
      const scored = pool
        .map((item) => ({ item, score: scoreItem(item, tokens) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score);
      if (!tokens.length) return pool.slice(0, 48);
      return scored.slice(0, 48).map(({ item }) => item);
    }

    async #searchStore(query) {
      const tokens = tokenize(query);
      if (this.storeEndpoint && query.trim().length > 1) {
        try {
          const url = new URL(this.#apiUrl(this.storeEndpoint), window.location.origin);
          url.searchParams.set('q', query.trim());
          url.searchParams.set('limit', '24');
          const res = await fetch(url.toString(), { headers: this.#apiHeaders() });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data.items)) return data.items;
          }
        } catch {
          /* local fallback */
        }
      }

      const scored = this.storeCatalog
        .map((item) => ({ item, score: scoreItem(item, tokens) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score);
      if (!tokens.length) return this.storeCatalog.slice(0, 24);
      return scored.slice(0, 24).map(({ item }) => item);
    }

    #deriveTrendingTags() {
      const counts = new Map();
      for (const seed of TRENDING_SEED) counts.set(seed, 100);
      for (const item of this.freeCatalog) {
        for (const tag of item.tags || []) {
          const key = String(tag).toLowerCase();
          counts.set(key, (counts.get(key) || 0) + 1);
        }
      }
      for (const item of this.storeCatalog) {
        for (const tag of item.tags || []) {
          const key = String(tag).toLowerCase();
          counts.set(key, (counts.get(key) || 0) + 1);
        }
      }
      this.trendingTags = [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([tag]) => tag);
      this.#renderTrendingTags();
    }

    #inspirationQuery() {
      if (this._activeTag) return this._activeTag;
      return (this.searchInput?.value || '').trim();
    }

    #renderTrendingTags() {
      if (!this.tagsEl || !this.tagsWrapEl) return;
      const show = this.tab === 'inspiration' || this.tab === 'similar' || this.tab === 'store';
      this.tagsWrapEl.hidden = !show;
      if (!show) return;
      this.tagsEl.replaceChildren();

      if (this.tab === 'inspiration') {
        const allBtn = document.createElement('button');
        allBtn.type = 'button';
        allBtn.className = 'ai-generator__design-hub-tag';
        allBtn.dataset.aiInspirationTag = '';
        allBtn.dataset.aiInspirationTagAll = 'true';
        allBtn.setAttribute('role', 'listitem');
        const showAll = !this._activeTag && !this.#inspirationQuery();
        allBtn.setAttribute('aria-pressed', showAll ? 'true' : 'false');
        if (showAll) allBtn.classList.add('is-active');
        allBtn.textContent = 'All';
        this.tagsEl.appendChild(allBtn);
      }

      for (const tag of this.trendingTags) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ai-generator__design-hub-tag';
        btn.dataset.aiInspirationTag = tag;
        btn.setAttribute('role', 'listitem');
        btn.setAttribute('aria-pressed', this._activeTag === tag ? 'true' : 'false');
        if (this._activeTag === tag) btn.classList.add('is-active');
        btn.textContent = tag.charAt(0).toUpperCase() + tag.slice(1);
        this.tagsEl.appendChild(btn);
      }
    }

    #apiUrl(endpoint) {
      if (!endpoint) return '';
      if (/^https?:\/\//i.test(endpoint)) return endpoint;
      const base = window.location.origin;
      return endpoint.startsWith('/') ? `${base}${endpoint}` : `${base}/${endpoint}`;
    }

    #apiHeaders() {
      const headers = { Accept: 'application/json' };
      if (this.storefrontKey) headers['X-WallPrint-Storefront-Key'] = this.storefrontKey;
      const libraryId = this.gen?.designLibraryCustomerId || this.gen?.customerId;
      if (libraryId) headers['X-WallPrint-Design-Library-Id'] = libraryId;
      return headers;
    }

    async #loadServerHistory() {
      if (!this.designLibraryEndpoint) return;
      try {
        const url = this.#apiUrl(this.designLibraryEndpoint);
        const res = await fetch(url, { headers: this.#apiHeaders(), credentials: 'same-origin' });
        if (!res.ok) return;
        const data = await res.json();
        if (!Array.isArray(data.items) || !data.items.length) return;
        this.#mergeServerHistory(data.items);
      } catch (err) {
        console.warn('[inspiration] design library', err);
      }
    }

    #mergeServerHistory(serverItems) {
      const removed = new Set(readRemovedHistoryIds());
      const local = readHistory().filter((item) => !removed.has(String(item.id || item.ai_image_id || '')));
      const byId = new Map();
      for (const item of [...serverItems, ...local]) {
        const id = item.id || item.ai_image_id;
        if (!id || removed.has(String(id))) continue;
        const prev = byId.get(id);
        const created = String(item.created_at || item.updated_at || '');
        const prevCreated = String(prev?.created_at || prev?.updated_at || '');
        if (!prev || created >= prevCreated) {
          byId.set(id, { ...item, source: 'history' });
        }
      }
      this.history = [...byId.values()]
        .sort((a, b) =>
          String(b.created_at || b.updated_at || '').localeCompare(
            String(a.created_at || a.updated_at || '')
          )
        )
        .slice(0, MAX_HISTORY);
      writeHistory(this.history);
      this.#syncHistoryCount();
      if (this.tab === 'history' || this.tab === 'similar') this.#render();
    }

    #removeHistoryItem(id) {
      const key = String(id || '').trim();
      if (!key) return;
      const removed = readRemovedHistoryIds();
      if (!removed.includes(key)) {
        removed.unshift(key);
        writeRemovedHistoryIds(removed);
      }
      this.history = this.history.filter((item) => String(item.id || item.ai_image_id || '') !== key);
      writeHistory(this.history);
      this.#syncHistoryCount();
      if (this.statusEl) this.statusEl.textContent = 'Removed from your design history.';
      if (this.tab === 'history' || this.tab === 'similar') this.#render();
    }

    /** Serve library thumbs through WallPrint (no third-party hotlinks). */
    #resolveImageUrl(item, size = 'thumb') {
      const direct = size === 'preview' ? item.preview_url || item.thumb_url : item.thumb_url || item.preview_url;
      if (direct && /\/inspiration\/image\//i.test(direct)) return this.#apiUrl(direct);
      if (item.source === 'history' || item.source === 'community') return direct || '';
      if (item.id && this.imageEndpoint) {
        const q = size === 'preview' ? '?size=preview' : '';
        return this.#apiUrl(`${this.imageEndpoint}/${encodeURIComponent(item.id)}${q}`);
      }
      return direct || '';
    }

    #resolveCardImageUrl(item) {
      const isHistory = item.source === 'history';
      return this.#resolveImageUrl(item, isHistory ? 'thumb' : 'preview');
    }

    #recordHistory(item) {
      const id = item.id || item.ai_image_id;
      if (!id) return;
      this.history = [item, ...this.history.filter((h) => h.id !== id)].slice(0, MAX_HISTORY);
      writeHistory(this.history);
      this.#syncHistoryCount();
      if (this.tab === 'history' || this.tab === 'similar') this.#render();
    }

    #sortFeaturedFirst(items) {
      return [...items].sort(
        (a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured)) || String(a.title || '').localeCompare(String(b.title || ''))
      );
    }

    #syncHistoryCount() {
      if (this.historyCountEl) {
        const n = this.history.length;
        this.historyCountEl.textContent = String(n);
        this.historyCountEl.hidden = n <= 0;
      }
      this.#syncSavedCount();
      this.#syncNavStrips();
    }

    #syncSavedCount() {
      const items = readSavedLibrary();
      if (this.savedCountEl) {
        this.savedCountEl.textContent = String(items.length);
        this.savedCountEl.hidden = items.length <= 0;
      }
    }

    #syncNavStrip(stripEl, items, max = 4) {
      if (!stripEl) return;
      stripEl.replaceChildren();
      const recent = items.slice(0, max);
      const thumbs = recent
        .map((item) => this.#resolveImageUrl(item, 'thumb'))
        .filter(Boolean);
      const show = this.expanded === false && thumbs.length > 0;
      stripEl.hidden = !show;
      stripEl.setAttribute('aria-hidden', show ? 'false' : 'true');
      for (const item of recent) {
        const thumb = this.#resolveImageUrl(item, 'thumb');
        if (!thumb) continue;
        const img = document.createElement('img');
        img.src = thumb;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        stripEl.appendChild(img);
      }
    }

    #similarPreviewItems() {
      if (!this._visualResults) return [];
      const visualHistory = this.#mergeVisualHistory(this._visualResults);
      const visualLibrary = this._visualResults.library || [];
      const seen = new Set();
      const items = [];
      for (const item of [...visualHistory, ...visualLibrary]) {
        const key = item.id || item.ai_image_id || item.prompt;
        if (!key || seen.has(key)) continue;
        if (this.visualQuery?.asset_id && key === this.visualQuery.asset_id) continue;
        seen.add(key);
        items.push(item);
        if (items.length >= 4) break;
      }
      return items;
    }

    async #prefetchSimilarPreviews() {
      if (!this.visualQuery) {
        this._visualResults = null;
        return;
      }
      await this.#fetchVisualSimilar('');
    }

    #syncNavStrips() {
      this.#syncNavStrip(this.historyStripEl, this.history);
      this.#syncNavStrip(
        this.savedStripEl,
        readSavedLibrary().map((d) => ({
          id: d.id,
          thumb_url: d.previewUrl,
          preview_url: d.previewUrl,
          source: 'saved',
        }))
      );
      this.#syncNavStrip(
        this.inspirationStripEl,
        this.#mergeInspirationWithCommunity(this.freeCatalog, this.communityCatalog).slice(0, 4)
      );
      this.#syncNavStrip(this.similarStripEl, this.#similarPreviewItems());
      this.#syncNavStrip(this.storeStripEl, this.storeCatalog);
    }

    #syncFootnote() {
      if (!this.footnoteEl) return;
      const notes = {
        history: 'Restore your own generated designs from History. Remove any you no longer need.',
        saved: 'Saved designs include wall size and pricing — connected to the Save button on the toolbar.',
        similar: 'Visual matches for your current design — use prompts to iterate.',
        inspiration:
          'WallPrint community picks appear first — order a print or use prompts as reference. Stock inspiration images are not loaded into preview.',
        store:
          'Curated premium selection of artwork from WallPrint artists and photographers — use a piece as your starting prompt or open the product page.',
      };
      this.footnoteEl.textContent = notes[this.tab] || notes.inspiration;
    }

    #syncTabChrome() {
      if (this.searchWrapEl) {
        this.searchWrapEl.hidden = this.tab === 'history' || this.tab === 'saved';
      }
      this.#syncFootnote();
      this.#renderTrendingTags();
    }

    #setExpanded(open) {
      this.expanded = Boolean(open);
      this.root.classList.toggle('is-collapsed', !this.expanded);
      this.genRoot?.classList.toggle('is-hub-open', this.expanded);
      if (this.headToggle) {
        this.headToggle.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
      }
      const headHint = this.root.querySelector('[data-ai-design-hub-head-hint]');
      if (headHint instanceof HTMLElement) {
        headHint.hidden = !this.expanded;
      }
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
        const label = this.toggleBtn.querySelector('.ai-generator__design-hub-toggle-label');
        if (label) label.textContent = this.expanded ? 'Close library' : 'Open library';
      }
      this.#syncPanelPlacement();
      this.#syncNavStrips();
      this.#bindPanelResizeIfNeeded();
      if (!this.expanded) {
        this.genRoot?.classList.remove('is-hub-overlay-open');
        this.genRoot?.classList.remove('is-hub-in-preview');
      }
      this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      window.requestAnimationFrame(() => {
        this.genRoot?.dispatchEvent(new CustomEvent('wallprint:hub-placement-changed'));
      });
    }

    #maybeAutoExpand(items, visualData) {
      if (this.expanded || !items.length) return;
      const hasVisual = Boolean(visualData?.count && this.visualQuery);
      if (this.tab === 'history' && this.history.length >= 1) {
        this.#setExpanded(true);
        return;
      }
      if (hasVisual || (this.visualQuery && items.length >= 3)) {
        this.#setExpanded(true);
      }
    }

    async #searchFree(query) {
      const tokens = tokenize(query);
      const hasColorFilter = Boolean(this._activePalette?.primary);
      if (this.searchEndpoint && (query.trim().length > 1 || hasColorFilter)) {
        try {
          const url = new URL(this.#apiUrl(this.searchEndpoint), window.location.origin);
          if (query.trim().length > 1) url.searchParams.set('q', query.trim());
          url.searchParams.set('limit', '48');
          if (hasColorFilter) {
            url.searchParams.set('color', this._activePalette.primary);
          }
          const res = await fetch(url.toString(), { headers: this.#apiHeaders() });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data.results)) return data.results;
          }
        } catch {
          /* local fallback */
        }
      }

      const pool = this.freeCatalog.filter((item) => item.cached !== false);
      const scored = pool
        .map((item) => ({ item, score: scoreItem(item, tokens) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score);
      if (!tokens.length) return pool;
      return scored.map(({ item }) => item);
    }

    #mergeInspirationWithCommunity(inspirationItems, communityItems) {
      const seen = new Set();
      const merged = [];
      const communitySorted = [...communityItems].sort(
        (a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured))
      );
      for (const item of communitySorted) {
        const key = item.id || item.ai_image_id;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push(item);
      }
      for (const item of inspirationItems) {
        const key = item.id || item.ai_image_id || item.prompt;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push(item);
      }
      return merged.slice(0, 48);
    }

    #searchSaved(query) {
      const tokens = tokenize(query);
      const library = readSavedLibrary();
      if (!tokens.length) return library.slice(0, 24);
      return library
        .filter((item) => scoreText([item.label, item.measurements?.wallSize, item.measurements?.printSize].join(' '), tokens) > 0)
        .slice(0, 24);
    }

    #searchHistory(query) {
      const tokens = tokenize(query);
      const palette = this._activePalette;
      const scored = this.history
        .map((item) => {
          const textScore = scoreItem(item, tokens);
          const colorScore = palette ? scorePaletteMatch(item, palette) : 0;
          const score = palette && !tokens.length ? colorScore : textScore + colorScore * 0.5;
          return { item, score, colorScore };
        })
        .filter(({ score, colorScore }) => {
          if (palette && !tokens.length) return colorScore >= 0.12;
          return score > 0;
        })
        .sort(
          (a, b) =>
            (b.item.created_at || '').localeCompare(a.item.created_at || '') ||
            b.score - a.score ||
            b.colorScore - a.colorScore
        );
      if (!tokens.length && !palette) return this.history.slice(0, 24);
      if (!tokens.length && palette) {
        return scored.slice(0, 24).map(({ item }) => item);
      }
      return scored.slice(0, 24).map(({ item }) => item);
    }

    #historyRefs() {
      return this.history
        .map((item) => {
          const assetId = item.ai_image_id || item.id;
          if (!assetId || String(assetId).startsWith('local_')) return null;
          return {
            kind: String(assetId).startsWith('upload_') ? 'uploads' : 'assets',
            asset_id: assetId,
          };
        })
        .filter(Boolean);
    }

    async #fetchVisualSimilar(query) {
      if (!this.similarEndpoint || !this.visualQuery) {
        this._visualResults = null;
        return null;
      }

      const excludeId = this.visualQuery.asset_id;
      try {
        let res;
        const historyRefs = this.#historyRefs().filter((ref) => ref.asset_id !== excludeId);

        if (this.visualQuery.asset_id && !String(this.visualQuery.asset_id).startsWith('local_')) {
          res = await fetch(this.#apiUrl(this.similarEndpoint), {
            method: 'POST',
            headers: { ...this.#apiHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              asset_kind: this.visualQuery.kind,
              asset_id: this.visualQuery.asset_id,
              q: query || undefined,
              limit: 24,
              history_refs: historyRefs,
            }),
          });
        } else {
          const blob = await this.gen?.getPreviewImageBlob?.();
          if (!blob) {
            this._visualResults = null;
            return null;
          }
          const formData = new FormData();
          formData.append('image', blob, 'preview.jpg');
          if (query) formData.append('q', query);
          formData.append('limit', '24');
          formData.append('history_refs', JSON.stringify(historyRefs));
          res = await fetch(this.#apiUrl(this.similarEndpoint), {
            method: 'POST',
            headers: this.#apiHeaders(),
            body: formData,
          });
        }

        if (!res.ok) {
          this._visualResults = null;
          return null;
        }
        const data = await res.json();
        this._visualResults = data;
        return data;
      } catch (err) {
        console.warn('[inspiration] visual search', err);
        this._visualResults = null;
        return null;
      }
    }

    #mergeVisualHistory(visualData) {
      if (!visualData?.history?.length) return [];
      const byId = new Map(this.history.map((item) => [item.ai_image_id || item.id, item]));
      return visualData.history
        .map((match) => {
          const item = byId.get(match.asset_id);
          if (!item) return null;
          return {
            ...item,
            similarity: match.similarity,
            match: 'visual',
          };
        })
        .filter(Boolean);
    }

    async #render() {
      if (!this.grid) return;
      const token = ++this._renderToken;
      const query =
        this.tab === 'inspiration'
          ? this.#inspirationQuery()
          : (this.searchInput?.value || this.gen?.promptInput?.value || '').trim();
      this.#syncHistoryCount();
      this.#syncTabChrome();

      const visualData =
        this.tab === 'similar' && this.visualQuery ? await this.#fetchVisualSimilar(query) : null;
      if (token !== this._renderToken) return;
      const visualLibrary = visualData?.library || [];
      const visualHistory = this.#mergeVisualHistory(visualData);

      let items = [];
      if (this.tab === 'history') {
        items = this.#searchHistory(query);
      } else if (this.tab === 'saved') {
        items = this.#searchSaved(query);
      } else if (this.tab === 'similar') {
        if (query) {
          const textHistory = this.#searchHistory(query);
          const seen = new Set();
          items = [];
          for (const item of [...visualHistory, ...visualLibrary, ...textHistory]) {
            const key = item.id || item.ai_image_id || item.prompt;
            if (!key || seen.has(key)) continue;
            seen.add(key);
            items.push(item);
            if (items.length >= 24) break;
          }
        } else {
          items = [...visualHistory, ...visualLibrary];
          const seen = new Set();
          if (this.visualQuery?.asset_id) seen.add(this.visualQuery.asset_id);
          items = items.filter((item) => {
            const key = item.id || item.ai_image_id;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        }
      } else if (this.tab === 'inspiration') {
        const community = await this.#searchCommunity(query);
        let inspiration = visualLibrary.length && !query ? visualLibrary : await this.#searchFree(query);
        if (!query) inspiration = this.#sortFeaturedFirst(inspiration);
        items = this.#mergeInspirationWithCommunity(inspiration, community);
      } else if (this.tab === 'store') {
        items = await this.#searchStore(query);
      } else {
        items = await this.#searchFree(query);
      }

      if (token !== this._renderToken) return;

      this.grid.replaceChildren();
      if (!items.length) {
        if (this.emptyEl) this.emptyEl.hidden = false;
        if (this.statusEl) {
          this.statusEl.textContent =
            this.tab === 'saved' && !readSavedLibrary().length
              ? 'Nothing saved yet — generate or upload a design, add wall measurements, then tap Save on the toolbar.'
              : this.tab === 'history' && !this.history.length
              ? 'Your generated and uploaded designs will appear here — saved to your account on the server.'
              : this.tab === 'similar' && !this.visualQuery
                ? 'Generate or upload a design to unlock visual matches.'
                : this.tab === 'inspiration' && !this.communityCatalog.length && !this.freeCatalog.length
                  ? 'Inspiration and WallPrint picks could not be loaded — check your connection or try again.'
                  : this.tab === 'store' && !this.storeCatalog.length
                  ? 'Curated artwork could not be loaded — check your connection or try again.'
                  : '';
        }
        return;
      }

      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.statusEl) {
        if (visualData?.count && this.visualQuery && !query) {
          this.statusEl.textContent = `${items.length} visually similar design${items.length === 1 ? '' : 's'}`;
        } else if (query) {
          this.statusEl.textContent = `${items.length} result${items.length === 1 ? '' : 's'} for “${query}”`;
        } else if (this.visualQuery) {
          this.statusEl.textContent = `${items.length} match${items.length === 1 ? '' : 'es'} for your design`;
        } else if (this.tab === 'saved') {
          this.statusEl.textContent = `${items.length} saved design${items.length === 1 ? '' : 's'}`;
        } else if (this.tab === 'store') {
          this.statusEl.textContent = `${items.length} curated artwork${items.length === 1 ? '' : 's'}`;
        } else if (this.tab === 'inspiration') {
          const communityCount = items.filter((item) => item.source === 'community').length;
          this.statusEl.textContent = communityCount
            ? `${items.length} inspiration${items.length === 1 ? '' : 's'} · ${communityCount} WallPrint pick${communityCount === 1 ? '' : 's'}`
            : `${items.length} inspiration${items.length === 1 ? '' : 's'}`;
        } else {
          this.statusEl.textContent = `${items.length} inspiration${items.length === 1 ? '' : 's'}`;
        }
      }

      for (const item of items) {
        this.grid.appendChild(this.tab === 'saved' ? this.#buildSavedCard(item) : this.#buildCard(item));
      }

      if (this.tab === 'similar' && visualData) {
        this.#syncNavStrips();
      }

      this.#maybeAutoExpand(items, visualData);
    }

    #buildSavedCard(item) {
      const li = document.createElement('article');
      li.className = 'ai-generator__hub-card is-hub-saved';
      li.setAttribute('role', 'listitem');
      li.dataset.aiSavedItem = JSON.stringify(item);
      const thumb = item.previewUrl || '';
      const title = item.label || 'Saved design';
      li.innerHTML = `
        <button type="button" class="ai-generator__hub-card-visual" data-ai-inspiration-action="restore-saved" data-ai-saved-id="${this.#escape(item.id)}">
          <img src="${this.#escape(thumb)}" alt="" loading="lazy" decoding="async" width="220" height="280">
          <span class="ai-generator__hub-card-glass" aria-hidden="true"></span>
          <span class="ai-generator__hub-card-shade" aria-hidden="true"></span>
          <span class="ai-generator__hub-card-badge">Saved</span>
        </button>
        <div class="ai-generator__hub-card-body">
          <p class="ai-generator__hub-card-title">${this.#escape(title)}</p>
          <p class="ai-generator__hub-card-prompt">${this.#escape(item.measurements?.wallSize || '')} · ${this.#escape(item.priceLabel || '')}</p>
          <div class="ai-generator__hub-card-actions">
            <button type="button" class="ai-generator__hub-card-btn" data-ai-inspiration-action="restore-saved" data-ai-saved-id="${this.#escape(item.id)}">Restore</button>
            <button type="button" class="ai-generator__hub-card-btn ai-generator__hub-card-btn--ghost" data-ai-inspiration-action="compare-saved" data-ai-saved-id="${this.#escape(item.id)}">Compare</button>
            <button type="button" class="ai-generator__hub-card-btn ai-generator__hub-card-btn--ghost" data-ai-inspiration-action="remove-saved" data-ai-saved-id="${this.#escape(item.id)}">Remove</button>
          </div>
        </div>
      `;
      return li;
    }

    #buildCard(item) {
      const li = document.createElement('article');
      li.className = 'ai-generator__hub-card';
      li.setAttribute('role', 'listitem');
      li.dataset.aiInspirationItem = 'true';
      li.dataset.inspirationId = item.id || '';
      li.dataset.inspirationSource = item.source || 'free';

      const thumb = this.#resolveCardImageUrl(item);
      const title = item.title || item.prompt?.slice(0, 48) || 'Inspiration';
      const isHistory = item.source === 'history';
      const isStore = item.source === 'store';
      const isCommunity = item.source === 'community';
      if (isHistory) li.classList.add('is-hub-yours');
      else if (isStore) li.classList.add('is-hub-store');
      else if (isCommunity) li.classList.add('is-hub-community');
      else li.classList.add('is-hub-reference');
      const badge = isHistory
        ? item.match === 'visual'
          ? 'Similar'
          : 'Your design'
        : isStore
          ? 'Premium'
          : isCommunity
            ? item.creator_name === 'WallPrint' || !item.creator_name
              ? 'WallPrint'
              : 'Community'
            : item.match === 'visual'
              ? 'Match'
              : 'Prompt ref';
      const creatorLine =
        isCommunity && item.creator_name
          ? `<p class="ai-generator__hub-card-creator">${this.#escape(item.creator_name)}</p>`
          : '';
      const similarity =
        typeof item.similarity === 'number' ? `${Math.round(item.similarity * 100)}% match` : '';

      li.innerHTML = `
        <div class="ai-generator__hub-card-visual-wrap">
          <button type="button" class="ai-generator__hub-card-visual" data-ai-inspiration-action="prompt">
            <img src="${this.#escape(thumb)}" alt="" loading="lazy" decoding="async" width="220" height="280">
            <span class="ai-generator__hub-card-glass" aria-hidden="true"></span>
            <span class="ai-generator__hub-card-shade" aria-hidden="true"></span>
            <span class="ai-generator__hub-card-badge">${this.#escape(badge)}</span>
            ${similarity ? `<span class="ai-generator__hub-card-score">${this.#escape(similarity)}</span>` : ''}
          </button>
          ${
            isHistory
              ? `<button type="button" class="ai-generator__hub-card-delete" data-ai-inspiration-action="delete" aria-label="Remove from history" title="Remove from history">×</button>`
              : ''
          }
        </div>
        <div class="ai-generator__hub-card-body">
          <p class="ai-generator__hub-card-title">${this.#escape(title)}</p>
          ${creatorLine}
          <p class="ai-generator__hub-card-prompt">${this.#escape(item.prompt || '')}</p>
          <div class="ai-generator__hub-card-actions">
            <button type="button" class="ai-generator__hub-card-btn" data-ai-inspiration-action="prompt">${isCommunity ? 'Create yours' : 'Use prompt'}</button>
            ${
              isHistory && item.preview_url
                ? '<button type="button" class="ai-generator__hub-card-btn ai-generator__hub-card-btn--ghost" data-ai-inspiration-action="preview">Restore</button>'
                : isCommunity && item.ai_image_id
                  ? '<button type="button" class="ai-generator__hub-card-btn ai-generator__hub-card-btn--ghost" data-ai-inspiration-action="order">Order this design</button>'
                  : isStore && item.product_url
                    ? '<button type="button" class="ai-generator__hub-card-btn ai-generator__hub-card-btn--ghost" data-ai-inspiration-action="product">View product</button>'
                    : isCommunity && item.shop_url
                      ? '<button type="button" class="ai-generator__hub-card-btn ai-generator__hub-card-btn--ghost" data-ai-inspiration-action="shop">Shop WallPrint</button>'
                      : ''
            }
          </div>
        </div>
      `;

      li.dataset.itemJson = JSON.stringify(item);
      return li;
    }

    #itemFromCard(card) {
      try {
        return JSON.parse(card.dataset.itemJson || '{}');
      } catch {
        return null;
      }
    }

    #escape(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/"/g, '&quot;');
    }

    #applyPrompt(item) {
      if (!isLibraryItemSuitable(item)) {
        if (this.statusEl) {
          this.statusEl.textContent =
            'This inspiration entry was skipped — library prompts must be flat wallpaper patterns only, not rooms or people.';
        }
        return;
      }
      const prompt = scrubWallpaperLibraryPrompt(item.prompt || '');
      if (!prompt || hasForbiddenPromptTerms(prompt) || !this.gen?.promptInput) return;
      this.gen.promptInput.value = prompt;
      this.gen.promptInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.gen.setMode?.('generate');
      this.gen.promptInput.focus({ preventScroll: true });
      this.gen.promptInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      let status =
        item.source === 'history'
          ? 'Prompt applied from your history — Generate again if you want a fresh version.'
          : item.source === 'store'
            ? 'Prompt applied from curated artwork — Generate to create your own print-ready version.'
            : 'Prompt copied for reference only — Generate to create your own design (this image is not loaded into preview).';
      if (this.gen && !this.gen.hasPreviewShapeContext?.()) {
        this.gen.promptPreviewShapeSetup?.();
        status += ' Add wall measurements or pick an aspect ratio so the preview matches your print shape.';
      }
      if (this.statusEl) this.statusEl.textContent = status;
    }

    async #restoreHistoryItem(item) {
      if (!this.gen) return;
      const ok = await this.gen.restoreGenerationHistory?.(item);
      if (ok && this.statusEl) {
        const needsShape = !this.gen.hasPreviewShapeContext?.();
        this.statusEl.textContent = needsShape
          ? 'Design restored — add wall measurements or choose an aspect ratio so the preview matches your print shape.'
          : 'Restored your previous design preview.';
      }
    }

    async #orderCommunityItem(item) {
      if (!this.gen) return;
      const communityId = String(item.id || item.community_id || '').trim();
      if (!communityId) return;

      if (this.statusEl) {
        this.statusEl.textContent = 'Loading design for print…';
      }

      try {
        const base = this.communityGalleryEndpoint.replace(/\/$/, '');
        const res = await fetch(this.#apiUrl(`${base}/${encodeURIComponent(communityId)}/studio-load`));
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || data.error || 'Could not load this design.');
        }
        const ok = await this.gen.restoreGenerationHistory?.(data.item);
        if (!ok) {
          throw new Error('Could not open this design in the preview.');
        }
        if (this.statusEl) {
          const needsShape = !this.gen.hasPreviewShapeContext?.();
          const creator = data.item?.creator_name ? ` by ${data.item.creator_name}` : '';
          this.statusEl.textContent = needsShape
            ? `Design loaded${creator} — add wall measurements or choose an aspect ratio, then add to cart.`
            : `Design loaded${creator} — check wall size and add to cart when ready.`;
        }
        this.gen.scrollToStudioSection?.('design');
      } catch (err) {
        console.warn('[inspiration] community order load', err);
        if (this.statusEl) {
          this.statusEl.textContent = err.message || 'Could not load this design for ordering.';
        }
        this.gen.reportError?.(err.message || 'Could not load this design for ordering.');
      }
    }
  }

  const boot = (genRoot) => {
    if (!genRoot) return;
    const galleryEl = genRoot.querySelector('[data-ai-inspiration-gallery]');
    if (!galleryEl) return;
    if (!galleryEl.wallprintInspirationInstance) {
      galleryEl.wallprintInspirationInstance = new InspirationGallery(galleryEl);
    }
    galleryEl.wallprintInspirationInstance.gen = genRoot.wallprintAiGenerator ?? null;
  };

  const bootAll = () => {
    document.querySelectorAll('[data-ai-generator]').forEach((root) => boot(root));
  };

  document.addEventListener('wallprint:ai-generator-ready', (event) => {
    boot(/** @type {CustomEvent} */ (event).detail?.root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  document.addEventListener('shopify:section:load', bootAll);
})();
